/**
 * Statutory Registers — the eleven books every company must keep under the
 * Companies Act, 2013.
 *
 * Two screens in one page: the Title Sheet index of all eleven registers, and
 * one register open for editing. The index lists registers the company has
 * never opened as well as the ones it uses, because the Act requires them to be
 * maintained whether or not anything has happened in them — a list that showed
 * only the non-empty ones would quietly imply the rest do not apply.
 *
 * Two shapes of register, because the prescribed forms genuinely differ:
 * most are a header plus one table, while MGT-1, MGT-2 and the Directors & KMP
 * register are kept per member / holder / director, each folio carrying its own
 * details panel and its own transaction table.
 *
 * Everything here is editable by the company. These registers are correctable
 * by law and in practice — a misspelt member name is fixed in place, not
 * superseded — so unlike the UPSI register there is no append-only rule. The
 * protection is the audit trail behind every write, plus the provenance badge
 * that shows which rows came from MCA rather than from a person.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import AppLayout from '../components/layout/AppLayout';
import PageHeader from '../components/common/PageHeader';
import Button from '../components/common/Button';
import Chip from '../components/common/Chip';
import Modal from '../components/common/Modal';
import EmptyState from '../components/common/EmptyState';
import { labelClass, inputClass, textareaClass } from '../components/common/FormControls';
import {
  TbBook2, TbDownload, TbRefresh, TbArrowLeft, TbPlus, TbTrash, TbUsers,
  TbFileDescription, TbCloudDownload, TbAlertTriangle, TbDatabaseOff, TbCircleCheck,
  TbMessage,
} from 'react-icons/tb';
import { useApp } from '../context/AppContext';
import { useUI } from '../context/UIContext';
import { registersAPI } from '../services/api';
import RegisterGrid from '../components/registers/RegisterGrid';
import RegisterTitleSheet from '../components/registers/RegisterTitleSheet';
import RegisterSheet from '../components/registers/RegisterSheet';
import CopilotDock from '../chat/CopilotDock';

/* ── Small shared pieces ─────────────────────────────────────────────────── */

/**
 * The title block of a prescribed form: form number, register name, the
 * bracketed statutory citation, and the "maintained separately for each…" note
 * where the form carries one. Shown on screen as well as in the export, because
 * the citation is how a practitioner confirms they are in the right register.
 */
function FormTitleBlock({ definition }) {
  return (
    <div className="rounded-xl border-[1.5px] border-[#ddd6c6] bg-[#faf8f3] px-4 py-3 mb-4 flex items-start gap-3 flex-wrap">
      <span className="shrink-0 rounded-md bg-[#145f5f] px-2 py-1 text-[10px] font-bold uppercase tracking-[0.6px] text-white">
        {definition.formLabel}
      </span>
      <div className="min-w-0 flex-1">
        {/* The register's name is already the page title directly above, so it
            is not repeated here. What this block adds is the citation — the
            wording that prints on the form and that a practitioner checks to
            confirm they are in the right register. */}
        <div className="text-[11px] text-[#5c5445] leading-relaxed">{definition.citation}</div>
        {definition.note && (
          <div className="text-[11px] font-semibold text-[#8a6d2f] mt-1">{definition.note}</div>
        )}
      </div>
    </div>
  );
}

/** A label + free-text control for one header or folio-detail field. */
function FieldRow({ field, value, canEdit, onCommit }) {
  const [draft, setDraft] = useState(value ?? '');
  useEffect(() => { setDraft(value ?? ''); }, [value]);

  const commit = () => { if ((draft ?? '') !== (value ?? '')) onCommit(draft); };
  const Control = field.type === 'textarea' ? 'textarea' : 'input';

  return (
    <div>
      <label className={labelClass}>
        {field.label}
        {field.required && <span className="text-[#b3261e] ml-0.5">*</span>}
      </label>
      <Control
        className={field.type === 'textarea' ? textareaClass : inputClass}
        rows={field.type === 'textarea' ? 2 : undefined}
        // Dates stay free text: a register transcribes what was recorded, and a
        // date copied from a 2007 paper book must survive being entered as-is.
        placeholder={field.type === 'date' ? 'DD/MM/YYYY' : ''}
        value={draft}
        disabled={!canEdit}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
      />
    </div>
  );
}

/** Place / Date / Signature — the block at the foot of every prescribed form. */
function FooterBlock({ footer, signatoryLine, canEdit, onSave }) {
  const [draft, setDraft] = useState(footer);
  useEffect(() => { setDraft(footer); }, [footer]);

  const set = (key) => (e) => setDraft((d) => ({ ...d, [key]: e.target.value }));
  const commit = () => {
    if (JSON.stringify(draft) !== JSON.stringify(footer)) onSave(draft);
  };

  return (
    <div className="mt-6 rounded-xl border-[1.5px] border-[#ddd6c6] bg-white p-4">
      <div className="text-[12px] font-bold uppercase tracking-[0.6px] text-[#7a7060] mb-3">
        Authentication
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div>
          <label className={labelClass}>Place</label>
          <input className={inputClass} value={draft.place || ''} disabled={!canEdit}
            onChange={set('place')} onBlur={commit} />
        </div>
        <div>
          <label className={labelClass}>Date</label>
          <input className={inputClass} placeholder="DD/MM/YYYY" value={draft.date || ''} disabled={!canEdit}
            onChange={set('date')} onBlur={commit} />
        </div>
        <div>
          <label className={labelClass}>Signatory</label>
          <input className={inputClass} value={draft.signatoryName || ''} disabled={!canEdit}
            onChange={set('signatoryName')} onBlur={commit} />
        </div>
        <div>
          <label className={labelClass}>Designation</label>
          <input className={inputClass} placeholder={signatoryLine} value={draft.signatoryDesignation || ''}
            disabled={!canEdit} onChange={set('signatoryDesignation')} onBlur={commit} />
        </div>
      </div>
      <div className="text-[11px] text-[#8b8371] mt-2">
        Leave the designation blank to print the statutory line: {signatoryLine}.
      </div>
    </div>
  );
}

/* ── The result of filling from MCA ──────────────────────────────────────── */

/**
 * What the source is called in a sentence, so the copy reads naturally.
 *
 * `from` matters as much as the noun. The MCA dataset this system refreshes
 * from does not publish charges or directors — they reach a company record with
 * its MCA report when the company is added, or through its company details. So
 * "refresh MCA data and try again" would send someone to a button that cannot
 * help them.
 */
const SOURCE_NOUN = {
  charges: {
    one: 'charge',
    many: 'charges',
    where: 'the company’s filed charges',
    from: 'Charges reach a company record with its MCA report when the company is added, or through its company details — the MCA refresh does not publish them.',
  },
  directors: {
    one: 'director or KMP',
    many: 'directors and KMP',
    where: 'the company’s filed directors',
    from: 'Directors reach a company record with its MCA report when the company is added, or through its company details — the MCA refresh does not publish them.',
  },
};

/**
 * Turn the counts into the sentence the user actually needs.
 *
 * Three zeros is not a result — it is two completely different results wearing
 * the same face. "MCA has no charges for this company" means the master data
 * was never imported and the register must be typed by hand; "every charge is
 * already here" means the book is complete. Reporting only the counts left the
 * reader to work out which, from numbers that look identical.
 */
function describeSync(result, noun) {
  const { added, updated, available } = result;

  if (!available) {
    return {
      tone: 'empty',
      headline: `No ${noun.many} on this company’s record`,
      detail: `There was nothing to copy across, so the register is unchanged. ${noun.from} Add them there and run this again, or enter the ${noun.many} by hand.`,
    };
  }
  if (!added && !updated) {
    return {
      tone: 'complete',
      headline: 'Already up to date',
      detail: `All ${available} ${available === 1 ? noun.one : noun.many} from MCA are in this register, and every field they cover is filled. Nothing was changed.`,
    };
  }

  const parts = [];
  if (added) parts.push(`${added} ${added === 1 ? noun.one : noun.many} added`);
  if (updated) parts.push(`${updated} updated with missing details`);
  return {
    tone: 'changed',
    headline: parts.join(' · '),
    detail: `Read from ${noun.where}. Check the new entries and complete the columns MCA does not carry before treating the register as final.`,
  };
}

const TONES = {
  empty: { icon: TbDatabaseOff, fg: '#8a6d2f', bg: '#fdf8ec', border: '#e6d9b8' },
  complete: { icon: TbCircleCheck, fg: '#4a6b3f', bg: '#f2f7ef', border: '#cfdcc6' },
  changed: { icon: TbCircleCheck, fg: '#145f5f', bg: '#eef4f3', border: '#c4dcd9' },
};

/** One count. Muted at zero, so the eye lands on what actually happened. */
function Stat({ label, value }) {
  return (
    <div className="flex-1 min-w-0 rounded-lg border-[1.5px] border-[#e8e2d5] bg-white px-3 py-2.5 text-center">
      <div className={`text-[20px] font-bold tabular-nums leading-none ${value ? 'text-[#08111f]' : 'text-[#c4bbaa]'}`}>
        {value}
      </div>
      <div className="text-[10px] font-bold uppercase tracking-[0.5px] text-[#8b8371] mt-1.5 leading-tight">
        {label}
      </div>
    </div>
  );
}

function SyncResultModal({ result, definition, onClose }) {
  const noun = (result && SOURCE_NOUN[result.source]) || SOURCE_NOUN.charges;
  const summary = result ? describeSync(result, noun) : null;
  const tone = summary ? TONES[summary.tone] : TONES.changed;
  const Icon = tone.icon;

  return (
    <Modal
      open={Boolean(result)}
      onClose={onClose}
      title="Fill from MCA data"
      bodyClassName="px-6 py-5"
      footer={<Button variant="primary" onClick={onClose}>Done</Button>}
    >
      {result && summary && (
        <>
          <div
            className="rounded-xl border-[1.5px] px-4 py-3 flex items-start gap-3"
            style={{ background: tone.bg, borderColor: tone.border }}
          >
            <Icon size={20} style={{ color: tone.fg }} className="shrink-0 mt-0.5" />
            <div className="min-w-0">
              <div className="text-[14px] font-bold leading-snug" style={{ color: tone.fg }}>
                {summary.headline}
              </div>
              <div className="text-[12px] text-[#5c5445] mt-1 leading-relaxed">{summary.detail}</div>
            </div>
          </div>

          {/* Counts are support, not the message — so they only appear when
              there was something to count. */}
          {result.available > 0 && (
            <div className="flex gap-2 mt-4">
              <Stat label="Added" value={result.added} />
              <Stat label="Details filled" value={result.updated} />
              <Stat label="Unchanged" value={result.unchanged} />
            </div>
          )}

          {result.skipped > 0 && (
            <div className="mt-3 text-[11px] text-[#8a6d2f] leading-relaxed flex items-start gap-1.5">
              <TbAlertTriangle size={13} className="shrink-0 mt-0.5" />
              {result.skipped} {result.skipped === 1 ? 'record was' : 'records were'} skipped for
              having no {result.source === 'charges' ? 'charge ID or SRN' : 'DIN or name'} to
              identify {result.skipped === 1 ? 'it' : 'them'} by.
            </div>
          )}

          {result.available > 0 && (
            <div className="mt-4 pt-3 border-t border-[#e8e2d5] text-[11px] text-[#8b8371] leading-relaxed flex items-start gap-1.5">
              <TbFileDescription size={13} className="shrink-0 mt-0.5" />
              <span>
                Only blank fields are filled — anything you typed is left exactly as it was, so this
                is safe to run again.
                {definition?.autofillSource === 'charges'
                  ? ' The columns MCA does not carry (description of the property charged, terms and conditions, instrument creating the charge) stay for you to complete.'
                  : ' Shareholding, parentage and address columns are not in MCA data and stay for you to complete.'}
              </span>
            </div>
          )}
        </>
      )}
    </Modal>
  );
}

/**
 * The result of filling the whole book at once.
 *
 * One card per register that has a source, in the same words the single-register
 * sync uses — so "no charges in MCA data" and "already up to date" stay two
 * different answers here too — plus a line on the company's own particulars,
 * which is the part that reaches every form rather than just these two.
 */
function FillAllResultModal({ result, onClose }) {
  return (
    <Modal
      open={Boolean(result)}
      onClose={onClose}
      title="Fill from company data"
      bodyClassName="px-6 py-5"
      footer={<Button variant="primary" onClick={onClose}>Done</Button>}
    >
      {result && (
        <>
          <div className="space-y-3">
            {result.registers.map((entry) => {
              const noun = SOURCE_NOUN[entry.source] || SOURCE_NOUN.charges;
              const summary = describeSync(entry, noun);
              const tone = TONES[summary.tone];
              const Icon = tone.icon;
              return (
                <div
                  key={entry.registerType}
                  className="rounded-xl border-[1.5px] px-4 py-3 flex items-start gap-3"
                  style={{ background: tone.bg, borderColor: tone.border }}
                >
                  <Icon size={18} style={{ color: tone.fg }} className="shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <div className="text-[10px] font-bold uppercase tracking-[0.6px] text-[#8b8371]">
                      {entry.form} · {entry.title}
                    </div>
                    <div className="text-[13px] font-bold leading-snug mt-0.5" style={{ color: tone.fg }}>
                      {summary.headline}
                    </div>
                    <div className="text-[12px] text-[#5c5445] mt-1 leading-relaxed">{summary.detail}</div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-4 pt-3 border-t border-[#e8e2d5] text-[11px] text-[#8b8371] leading-relaxed flex items-start gap-1.5">
            <TbFileDescription size={13} className="shrink-0 mt-0.5" />
            <span>
              {result.identityFilled > 0
                ? `The company’s name and registered office were filled in on ${result.identityFilled} ${result.identityFilled === 1 ? 'register' : 'registers'} that print them. `
                : 'Every register that prints the company’s name and registered office already carries them. '}
              The downloaded workbook carries them on every form, including the registers with no
              entries yet. Only blank fields are filled, so nothing you typed was changed.
            </span>
          </div>
        </>
      )}
    </Modal>
  );
}

/* ── The index ───────────────────────────────────────────────────────────── */

function RegisterIndex({ company, items, onOpen, onExportBook, onFillAll, canEdit, busy }) {
  return (
    <>
      <div className="rounded-xl border-[1.5px] border-[#ddd6c6] bg-[#faf8f3] px-4 py-3 mb-5 text-[12px] text-[#5c5445] leading-relaxed">
        These eleven registers must be kept at the registered office for as long as the company
        exists, and produced for inspection on demand. Registers with no entries are listed here
        too — the duty to maintain them does not depend on anything having happened.
      </div>

      <RegisterTitleSheet company={company} items={items} onOpen={onOpen} />

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <div className="text-[11px] text-[#8b8371] leading-relaxed max-w-[520px]">
          The download is the prescribed workbook — a Title Sheet index and one sheet per register,
          in the form an inspector expects — filled with this company’s particulars and entries.
        </div>
        <div className="flex gap-2">
          {canEdit && (
            <Button variant="primary" onClick={onFillAll} disabled={busy}>
              <TbCloudDownload size={15} /> Fill from company data
            </Button>
          )}
          <Button variant="gold" onClick={onExportBook} disabled={busy}>
            <TbDownload size={15} /> Download all registers
          </Button>
        </div>
      </div>
    </>
  );
}

/* ── Folio registers ─────────────────────────────────────────────────────── */

/** The left-hand list of members / holders / directors. */
function FolioList({ definition, folios, activeId, onSelect, onAdd, canEdit }) {
  const nameKey = definition.folio.nameKey;
  return (
    <div className="rounded-xl border-[1.5px] border-[#ddd6c6] bg-white overflow-hidden">
      <div className="px-3 py-2.5 bg-[#f4f1e9] text-[11px] font-bold uppercase tracking-[0.5px] text-[#4a4335] flex items-center justify-between">
        <span>{definition.folio.entityLabel}s</span>
        <span className="tabular-nums text-[#8b8371]">{folios.length}</span>
      </div>

      <div className="max-h-[420px] overflow-y-auto">
        {folios.length === 0 && (
          <div className="px-3 py-5 text-[12px] text-[#8b8371] text-center">
            No {definition.folio.entityLabel.toLowerCase()} entered yet.
          </div>
        )}
        {folios.map((folio) => (
          <button
            key={folio.id}
            type="button"
            onClick={() => onSelect(folio.id)}
            className={`w-full text-left px-3 py-2.5 border-t border-[#e8e2d5] transition-colors
              ${activeId === folio.id ? 'bg-[#eef4f3]' : 'hover:bg-[#faf8f3]'}`}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-[12px] font-semibold text-[#08111f] truncate">
                {folio.details[nameKey] || `(unnamed ${definition.folio.entityLabel.toLowerCase()})`}
              </span>
              {folio.source === 'mca' && <Chip size="xs" tone='#4a6b3f'>MCA</Chip>}
            </div>
            <div className="text-[11px] text-[#8b8371] mt-0.5">
              {definition.folio.idLabel} {folio.folioNo || '—'} · {folio.entries.length} entr{folio.entries.length === 1 ? 'y' : 'ies'}
            </div>
          </button>
        ))}
      </div>

      {canEdit && (
        <button
          type="button"
          onClick={onAdd}
          className="w-full flex items-center justify-center gap-1.5 py-2.5 text-[12px] font-semibold
                     text-[#145f5f] hover:bg-[#f2f7f6] border-t-[1.5px] border-[#ddd6c6]"
        >
          <TbPlus size={14} /> Add {definition.folio.entityLabel.toLowerCase()}
        </button>
      )}
    </div>
  );
}

/** The details panel and transaction table for one folio. */
function FolioPanel({ definition, folio, canEdit, onUpdateDetail, onDelete, grid }) {
  return (
    <div className="space-y-5">
      <div className="rounded-xl border-[1.5px] border-[#ddd6c6] bg-white p-4">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <label className={labelClass}>{definition.folio.idLabel}</label>
            <input
              className={`${inputClass} max-w-[220px]`}
              value={folio.folioNo || ''}
              disabled={!canEdit}
              onChange={(e) => onUpdateDetail({ folioNo: e.target.value }, false)}
              onBlur={(e) => onUpdateDetail({ folioNo: e.target.value }, true)}
            />
          </div>
          {canEdit && (
            <Button size="xs" variant="danger" onClick={onDelete}>
              <TbTrash size={13} /> Remove
            </Button>
          )}
        </div>

        {definition.folio.panels.map((panel) => (
          <div key={panel.title} className="mb-5 last:mb-0">
            <div className="text-[11px] font-bold uppercase tracking-[0.6px] text-[#145f5f] mb-2.5 pb-1.5 border-b border-[#e8e2d5]">
              {panel.title}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {panel.fields.map((field) => (
                <div key={field.key} className={field.type === 'textarea' ? 'md:col-span-2 lg:col-span-3' : ''}>
                  <FieldRow
                    field={field}
                    value={folio.details[field.key] || ''}
                    canEdit={canEdit}
                    onCommit={(v) => onUpdateDetail({ details: { [field.key]: v } }, true)}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div>
        <div className="text-[12px] font-bold text-[#4a4335] mb-2">{definition.folio.tableTitle}</div>
        {grid}
      </div>
    </div>
  );
}

/* ── The page ────────────────────────────────────────────────────────────── */

export default function RegistersPage() {
  const { activeCompany } = useApp();
  const { showToast } = useUI();

  const companyId = activeCompany?._id || activeCompany?.id || null;

  const [index, setIndex] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  // The register currently open: which one, and which class of shares for the
  // registers the Rules require to be kept separately per class.
  const [open, setOpen] = useState(null); // { registerType, seriesKey }
  const [askOpen, setAskOpen] = useState(false);
  const [book, setBook] = useState(null); // { definition, register }
  const [activeFolioId, setActiveFolioId] = useState(null);
  const [activeTable, setActiveTable] = useState('main');

  const [seriesPrompt, setSeriesPrompt] = useState(null); // { item, value }
  const [syncResult, setSyncResult] = useState(null);
  // 'sheet' is the register as the prescribed document; 'edit' is the fields.
  const [view, setView] = useState('sheet');
  const [fillResult, setFillResult] = useState(null);

  const definition = book?.definition || null;
  const register = book?.register || null;

  // Read-only access is legitimate here — an assigned auditor or a diligence
  // reviewer needs to see the books without being able to alter them — so the
  // write controls come off rather than the page refusing to load. The company
  // list annotates rows with `myAccessLevel`; the single-company endpoint calls
  // the same value `accessLevel`, so both are read.
  const heldLevel = activeCompany?.myAccessLevel || activeCompany?.accessLevel || 'admin';
  const canEdit = heldLevel !== 'read';

  /* ── Loading ───────────────────────────────────────────────────────────── */

  const loadIndex = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const res = await registersAPI.list(companyId);
      setIndex(res.data?.registers || []);
    } catch (err) {
      showToast(err.message || 'Could not load the registers.', 'error');
    } finally {
      setLoading(false);
    }
  }, [companyId, showToast]);

  useEffect(() => { loadIndex(); }, [loadIndex]);

  const loadBook = useCallback(async (registerType, seriesKey) => {
    setBusy(true);
    try {
      const res = await registersAPI.get(companyId, registerType, seriesKey);
      setBook(res.data);
      setActiveTable(res.data?.definition?.tables?.[0]?.key || 'main');
      const folios = res.data?.register?.folios || [];
      setActiveFolioId((current) =>
        (folios.some((f) => f.id === current) ? current : folios[0]?.id || null));
      return res.data;
    } catch (err) {
      showToast(err.message || 'Could not open that register.', 'error');
      return null;
    } finally {
      setBusy(false);
    }
  }, [companyId, showToast]);

  /**
   * Every write returns the whole book, because the server recomputes running
   * balances across all rows on each save — patching a single row into local
   * state would leave the balances beneath it stale on screen.
   */
  const applyWrite = useCallback(async (fn, successMessage) => {
    setBusy(true);
    try {
      const res = await fn();
      setBook(res.data);
      if (successMessage) showToast(successMessage, 'success');
      return res.data;
    } catch (err) {
      showToast(err.message || 'That change could not be saved.', 'error');
      return null;
    } finally {
      setBusy(false);
    }
  }, [showToast]);

  /* ── Opening a register ────────────────────────────────────────────────── */

  const openRegister = useCallback((item, seriesKey = '') => {
    // A register opens as the document it is; editing is a deliberate switch.
    setView('sheet');
    // MGT-1 and MGT-2 are kept separately per class of shares, so opening one
    // without naming the class would create a nameless extra book.
    if (item.seriesLabel && !seriesKey) {
      if (item.books.length === 1) {
        setOpen({ registerType: item.id, seriesKey: item.books[0].seriesKey });
        loadBook(item.id, item.books[0].seriesKey);
        return;
      }
      // Asked once per class, not once per visit: a company's first book is
      // almost always its equity shares, so that is offered ready to accept.
      // Once a class exists, opening the register goes straight into it.
      setSeriesPrompt({ item, value: item.id === 'mgt-1' && !item.books.length ? 'EQUITY' : '' });
      return;
    }
    setOpen({ registerType: item.id, seriesKey });
    loadBook(item.id, seriesKey);
  }, [loadBook]);

  const closeRegister = useCallback(() => {
    setOpen(null);
    setBook(null);
    setActiveFolioId(null);
    setSyncResult(null);
    loadIndex();
  }, [loadIndex]);

  /* ── Writes ────────────────────────────────────────────────────────────── */

  const { registerType, seriesKey } = open || {};

  const saveHeader = (patch) =>
    applyWrite(() => registersAPI.updateHeader(companyId, registerType, seriesKey, patch));

  const saveFooter = (footer) =>
    applyWrite(() => registersAPI.updateFooter(companyId, registerType, seriesKey, footer));

  const addRow = (table) =>
    applyWrite(() => registersAPI.addEntry(companyId, registerType, seriesKey, { table, data: {} }));

  const updateRow = (row, patch) =>
    applyWrite(() => registersAPI.updateEntry(companyId, registerType, seriesKey, row.id, { data: patch }));

  const deleteRow = (row) => {
    if (!window.confirm('Delete this entry from the register? The deletion is recorded in the audit trail.')) return;
    applyWrite(() => registersAPI.deleteEntry(companyId, registerType, seriesKey, row.id));
  };

  /**
   * Take a derived column over by hand, or hand it back. The stored value is
   * kept either way, so switching back recomputes from the rows above.
   */
  const toggleOverride = (row, columnKey) => {
    const next = (row.overrides || []).includes(columnKey)
      ? (row.overrides || []).filter((k) => k !== columnKey)
      : [...(row.overrides || []), columnKey];
    applyWrite(() =>
      registersAPI.updateEntry(companyId, registerType, seriesKey, row.id, { data: {}, overrides: next }));
  };

  const addFolio = () =>
    applyWrite(() => registersAPI.addFolio(companyId, registerType, seriesKey, { details: {} }))
      .then((data) => {
        const folios = data?.register?.folios || [];
        if (folios.length) setActiveFolioId(folios[folios.length - 1].id);
      });

  const updateFolio = (folioId, patch) =>
    applyWrite(() => registersAPI.updateFolio(companyId, registerType, seriesKey, folioId, patch));

  const deleteFolio = (folioId) => {
    const label = definition?.folio?.entityLabel?.toLowerCase() || 'entry';
    if (!window.confirm(`Remove this ${label} and every entry in their folio? The deletion is recorded in the audit trail.`)) return;
    applyWrite(() => registersAPI.deleteFolio(companyId, registerType, seriesKey, folioId))
      .then((data) => setActiveFolioId(data?.register?.folios?.[0]?.id || null));
  };

  const addFolioRow = (folioId) =>
    applyWrite(() => registersAPI.addFolioEntry(companyId, registerType, seriesKey, folioId, { data: {} }));

  const updateFolioRow = (folioId, row, patch) =>
    applyWrite(() =>
      registersAPI.updateFolioEntry(companyId, registerType, seriesKey, folioId, row.id, { data: patch }));

  const deleteFolioRow = (folioId, row) => {
    if (!window.confirm('Delete this entry? The deletion is recorded in the audit trail.')) return;
    applyWrite(() => registersAPI.deleteFolioEntry(companyId, registerType, seriesKey, folioId, row.id));
  };

  const toggleFolioOverride = (folioId, row, columnKey) => {
    const next = (row.overrides || []).includes(columnKey)
      ? (row.overrides || []).filter((k) => k !== columnKey)
      : [...(row.overrides || []), columnKey];
    applyWrite(() =>
      registersAPI.updateFolioEntry(companyId, registerType, seriesKey, folioId, row.id, {
        data: {}, overrides: next,
      }));
  };

  /** Fill from MCA master data, then say plainly what changed. */
  const runSync = async () => {
    setBusy(true);
    try {
      const res = await registersAPI.sync(companyId, registerType, seriesKey);
      setBook(res.data);
      setSyncResult(res.data.sync);
    } catch (err) {
      showToast(err.message || 'Could not fill this register from MCA data.', 'error');
    } finally {
      setBusy(false);
    }
  };

  /**
   * Fill every register that has a source, from the company that is open — so a
   * company starting its books here does not have to visit eleven forms to find
   * the two that fill themselves. The index is reloaded afterwards because the
   * entry counts it shows have moved.
   */
  const runFillAll = async () => {
    setBusy(true);
    try {
      const res = await registersAPI.fillFromCompany(companyId);
      setFillResult(res.data.fill);
      await loadIndex();
    } catch (err) {
      showToast(err.message || 'Could not fill the registers from this company’s data.', 'error');
    } finally {
      setBusy(false);
    }
  };

  const exportOne = async () => {
    try {
      await registersAPI.exportRegister(companyId, registerType, seriesKey);
    } catch (err) {
      showToast(err.message || 'Export failed.', 'error');
    }
  };

  const exportBook = async () => {
    try {
      await registersAPI.exportBook(companyId);
      showToast('Downloading all eleven registers.', 'success');
    } catch (err) {
      showToast(err.message || 'Export failed.', 'error');
    }
  };

  /* ── Derived ───────────────────────────────────────────────────────────── */

  const activeFolio = useMemo(
    () => (register?.folios || []).find((f) => f.id === activeFolioId) || null,
    [register, activeFolioId],
  );

  const currentTable = useMemo(() => {
    if (!definition || definition.layout === 'folio') return null;
    return (definition.tables || []).find((t) => t.key === activeTable) || definition.tables?.[0] || null;
  }, [definition, activeTable]);

  /* ── Render ────────────────────────────────────────────────────────────── */

  if (!companyId) {
    return (
      <AppLayout>
        <PageHeader
          title="Statutory Registers"
          subtitle="The eleven registers required under the Companies Act, 2013"
        />
        <EmptyState
          icon={TbBook2}
          title="Select a company"
          subtitle="Registers are kept per company. Choose one from the company switcher to open its books."
        />
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      {!open ? (
        <>
          <PageHeader
            title="Statutory Registers"
            subtitle={`The eleven registers required under the Companies Act, 2013 · ${activeCompany?.name || ''}`}
            actions={
              <Button variant="ink" onClick={loadIndex} disabled={loading}>
                <TbRefresh size={15} /> Refresh
              </Button>
            }
          />
          {loading && !index.length
            ? <div className="text-[13px] text-[#7a7060]">Loading registers…</div>
            : (
              <RegisterIndex
                company={activeCompany}
                items={index}
                onOpen={openRegister}
                onExportBook={exportBook}
                onFillAll={runFillAll}
                canEdit={canEdit}
                busy={busy}
              />
            )}
        </>
      ) : (
        <>
          <PageHeader
            title={definition?.title || 'Register'}
            subtitle={
              definition
                ? `${definition.form}${register?.seriesKey ? ` · ${register.seriesKey}` : ''} · ${definition.section} & ${definition.rule}`
                : ''
            }
            actions={
              <>
                <Button variant="ink" onClick={closeRegister}>
                  <TbArrowLeft size={15} /> All registers
                </Button>
                {definition?.autofillSource && canEdit && (
                  <Button variant="primary" onClick={runSync} disabled={busy}>
                    <TbCloudDownload size={15} /> Fill from MCA
                  </Button>
                )}
                {/* The register as the document it is, or as the fields you
                    type into. Same book, two ways of looking at it. */}
                <div className="flex rounded-lg border-[1.5px] border-[#ddd6c6] overflow-hidden">
                  {[['sheet', 'Sheet'], ['edit', 'Fields']].map(([key, text]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setView(key)}
                      className={`px-3 py-1.5 text-[12px] font-semibold transition-colors
                        ${view === key
                          ? 'bg-[#145f5f] text-white'
                          : 'bg-white text-[#7a7060] hover:bg-[#f4f1e9]'}`}
                    >
                      {text}
                    </button>
                  ))}
                </div>
                <Button
                  variant={askOpen ? 'primary' : 'ink'}
                  onClick={() => setAskOpen((v) => !v)}
                  disabled={!definition}
                >
                  <TbMessage size={15} /> Ask Vee
                </Button>
                <Button variant="gold" onClick={exportOne} disabled={busy || !definition}>
                  <TbDownload size={15} /> Download form
                </Button>
              </>
            }
          />

          {/* Ask Vee, beside the open register.

              The dock is the SAME conversation as the AI Agent page, and the
              bridge below is the whole integration: getPageContext names the
              register on screen, so "add a member" needs no repetition of which
              book, and handleClientActions reloads it after Vee writes — a
              register that is a turn behind what was recorded is worse than one
              that never changed, because the user reads it as the truth. */}
          {askOpen && definition && (
            <div className="fixed right-0 top-0 bottom-0 w-[420px] max-w-[92vw] z-40 border-l bg-white shadow-[-8px_0_28px_-18px_rgba(8,17,31,0.4)]" style={{ borderColor: '#e2d9c8' }}>
              {/* historyDraftKind is deliberately unset: the registers page is
                  not a drafting studio with a document kind of its own, and
                  naming one would filter the in-dock history to a kind no
                  conversation has — an always-empty list. Unset, this behaves
                  like the AI Agent surface: one shared chat, full history. */}
              <CopilotDock
                title="Ask Vee"
                onClose={() => setAskOpen(false)}
                composerPlaceholder={`Add an entry to ${definition.form}, or ask about it…`}
                emptyHint={`This is ${definition.title || definition.form}. Ask me what it holds, or tell me an entry to add.`}
                getPageContext={() => (open ? {
                  page: 'registers',
                  registerType: open.registerType,
                  seriesKey: open.seriesKey || '',
                  registerName: definition.title || definition.form,
                } : null)}
                onClientActions={(actions) => {
                  // Vee writes straight into the book, so the page has to catch
                  // up in the same beat the reply lands.
                  if ((actions || []).some((a) => a && a.type === 'refresh_register')) {
                    loadBook(open.registerType, open.seriesKey);
                  }
                }}
              />
            </div>
          )}

          {!definition ? (
            <div className="text-[13px] text-[#7a7060]">Opening register…</div>
          ) : view === 'sheet' ? (
            <>
              <RegisterSheet
                definition={definition}
                register={register}
                canEdit={canEdit}
                on={{
                  saveHeader,
                  saveFooter,
                  addRow,
                  updateRow,
                  deleteRow,
                  toggleOverride,
                  addFolio,
                  updateFolio,
                  deleteFolio,
                  addFolioRow,
                  updateFolioRow,
                  deleteFolioRow,
                  toggleFolioOverride,
                }}
              />
              <div className="mt-3 text-[11px] text-[#8b8371] leading-relaxed">
                This is the register as it prints and as it downloads — the prescribed form, with
                this company’s particulars and entries in it.
                {canEdit && ' Type straight into it; each cell saves when you leave it.'}
              </div>
            </>
          ) : (
            <>
              <FormTitleBlock definition={definition} />

              {!canEdit && (
                <div className="rounded-xl border-[1.5px] border-[#e6d9b8] bg-[#fdf8ec] px-4 py-2.5 mb-5 text-[12px] text-[#7a5c1e] flex items-center gap-2">
                  <TbAlertTriangle size={15} />
                  You have read-only access to this company, so this register cannot be changed here.
                </div>
              )}

              {/* Identity and register-level header fields */}
              {(definition.identityFields.length > 0 || definition.header.length > 0) && (
                <div className="rounded-xl border-[1.5px] border-[#ddd6c6] bg-white p-4 mb-5">
                  {/* Says plainly which of these the system knows and which the
                      company must enter — otherwise a blank buy-back date looks
                      like a fill that failed rather than a fact nobody holds. */}
                  {definition.identityFields.length > 0 && (
                    <div className="text-[11px] text-[#8b8371] leading-relaxed mb-3">
                      The company’s name and registered office are filled from{' '}
                      {activeCompany?.name || 'the selected company'}.
                      {definition.autofillSource
                        ? ' The entries below fill from its MCA master data.'
                        : ' The rest of this form is not in the company record and is entered here.'}
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {[...definition.identityFields, ...definition.header].map((field) => (
                      <div key={field.key} className={field.type === 'textarea' ? 'md:col-span-2 lg:col-span-3' : ''}>
                        <FieldRow
                          field={field}
                          value={register.header[field.key] || ''}
                          canEdit={canEdit}
                          onCommit={(v) => saveHeader({ [field.key]: v })}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {definition.layout === 'folio' ? (
                <div className="grid grid-cols-1 lg:grid-cols-[280px_minmax(0,1fr)] gap-5">
                  <FolioList
                    definition={definition}
                    folios={register.folios}
                    activeId={activeFolioId}
                    onSelect={setActiveFolioId}
                    onAdd={addFolio}
                    canEdit={canEdit}
                  />

                  {activeFolio ? (
                    <FolioPanel
                      definition={definition}
                      folio={activeFolio}
                      canEdit={canEdit}
                      onDelete={() => deleteFolio(activeFolio.id)}
                      onUpdateDetail={(patch, commit) => { if (commit) updateFolio(activeFolio.id, patch); }}
                      grid={
                        <RegisterGrid
                          columns={definition.folio.columns}
                          rows={activeFolio.entries}
                          canEdit={canEdit}
                          onAddRow={() => addFolioRow(activeFolio.id)}
                          onUpdateRow={(row, patch) => updateFolioRow(activeFolio.id, row, patch)}
                          onDeleteRow={(row) => deleteFolioRow(activeFolio.id, row)}
                          onToggleOverride={(row, key) => toggleFolioOverride(activeFolio.id, row, key)}
                          emptyLabel="No transactions recorded for this folio yet."
                        />
                      }
                    />
                  ) : (
                    <EmptyState
                      variant="inline"
                      icon={TbUsers}
                      title={`No ${definition.folio.entityLabel.toLowerCase()} yet`}
                      subtitle={`This register is maintained separately for each ${definition.folio.entityLabel.toLowerCase()}. Add one to begin.`}
                    />
                  )}
                </div>
              ) : (
                <>
                  {/* MBP-4 is the only form with two tables — Part A contracts,
                      Part B the standing list of bodies corporate. */}
                  {definition.tables.length > 1 && (
                    <div className="flex gap-2 mb-4 flex-wrap">
                      {definition.tables.map((table) => (
                        <button
                          key={table.key}
                          type="button"
                          onClick={() => setActiveTable(table.key)}
                          className={`px-3 py-1.5 rounded-lg text-[12px] font-semibold border-[1.5px] transition-colors
                            ${activeTable === table.key
                              ? 'border-[#145f5f] bg-[#eef4f3] text-[#145f5f]'
                              : 'border-[#ddd6c6] bg-white text-[#7a7060] hover:border-[#cdc4b0]'}`}
                        >
                          Part {table.key}
                        </button>
                      ))}
                    </div>
                  )}

                  {currentTable?.title && (
                    <div className="text-[12px] font-semibold text-[#4a4335] mb-2.5 leading-relaxed">
                      {currentTable.title}
                    </div>
                  )}

                  {currentTable && (
                    <RegisterGrid
                      columns={currentTable.columns}
                      rows={register.entries.filter((e) => (e.table || 'main') === currentTable.key)}
                      canEdit={canEdit}
                      onAddRow={() => addRow(currentTable.key)}
                      onUpdateRow={updateRow}
                      onDeleteRow={deleteRow}
                      onToggleOverride={toggleOverride}
                    />
                  )}

                  {definition.footnotes.length > 0 && (
                    <div className="mt-3 space-y-1">
                      {definition.footnotes.map((note) => (
                        <div key={note} className="text-[11px] text-[#8b8371] leading-relaxed">{note}</div>
                      ))}
                    </div>
                  )}
                </>
              )}

              <FooterBlock
                footer={register.footer}
                signatoryLine={definition.signatoryLine}
                canEdit={canEdit}
                onSave={saveFooter}
              />
            </>
          )}
        </>
      )}

      {/* Naming the class of shares, for the registers the Rules require to be
          kept separately per class. */}
      <Modal
        open={Boolean(seriesPrompt)}
        onClose={() => setSeriesPrompt(null)}
        title={seriesPrompt ? `Open ${seriesPrompt.item.form}` : ''}
        bodyClassName="px-6 py-5"
        footer={
          <>
            <Button variant="ink" onClick={() => setSeriesPrompt(null)}>Cancel</Button>
            <Button
              variant="primary"
              disabled={!seriesPrompt?.value?.trim()}
              onClick={() => {
                const { item, value } = seriesPrompt;
                setSeriesPrompt(null);
                openRegister(item, value.trim());
              }}
            >
              Open register
            </Button>
          </>
        }
      >
        {seriesPrompt && (
          <>
            <p className="text-[12px] text-[#5c5445] leading-relaxed mb-3">
              {seriesPrompt.item.form} is maintained separately for each{' '}
              {seriesPrompt.item.seriesLabel.toLowerCase()} — the Rules require one book per class,
              and the class prints on the form itself. It is asked once: after this,{' '}
              {seriesPrompt.item.form} opens straight into that book, and you are only asked again
              if the company keeps more than one class.
            </p>
            {seriesPrompt.item.books.length > 0 && (
              <div className="mb-3">
                <div className={labelClass}>Already open</div>
                <div className="flex gap-1.5 flex-wrap">
                  {seriesPrompt.item.books.map((b) => (
                    <button
                      key={b.seriesKey}
                      type="button"
                      onClick={() => {
                        const item = seriesPrompt.item;
                        setSeriesPrompt(null);
                        openRegister(item, b.seriesKey);
                      }}
                      className="px-2.5 py-1 rounded-lg border-[1.5px] border-[#ddd6c6] bg-white
                                 text-[12px] font-semibold text-[#145f5f] hover:border-[#145f5f]"
                    >
                      {b.seriesKey || '—'}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <label className={labelClass}>{seriesPrompt.item.seriesLabel}</label>
            <input
              className={inputClass}
              autoFocus
              placeholder="e.g. EQUITY"
              value={seriesPrompt.value}
              onChange={(e) => setSeriesPrompt((p) => ({ ...p, value: e.target.value }))}
            />
          </>
        )}
      </Modal>

      {/* What the MCA fill actually did. A register is a legal record, so an
          automated pass reports rather than leaving the company to diff it. */}
      <SyncResultModal
        result={syncResult}
        definition={definition}
        onClose={() => setSyncResult(null)}
      />

      <FillAllResultModal result={fillResult} onClose={() => setFillResult(null)} />
    </AppLayout>
  );
}
